import { HttpAuth } from '@core/services/http-auth';
import { Injectable } from '@angular/core';
import { environment } from '@src/environments/environment';
import {
  BillingResponse,
  ChangeSubscriptionRequest,
  CommunicationRequest,
  CommunicationResponse,
  ContractResponse,
  CostBreakdownResponse,
  CustomerInvoiceResponse,
  CustomerInvoiceResponsePagedResponse,
  DashboardResponse,
  InvoiceCallDetailsResponse,
  InvoiceInternetDetailsResponse,
  NetworkSettingsRequest,
  NetworkSettingsResponse,
  NewSubscriptionsResponse,
  PrivacyResponse,
  SimCardTypeResponse,
  SubscriptionAvailabilityResponse,
  SubscriptionResponse,
  WalletResponse,
} from '@core/contracts/data-contracts';
import { map } from 'rxjs';
import { DataOutsideEUResponse } from '@core/models';
import { PortingOption } from '@core/enums';

@Injectable({
  providedIn: 'root',
})
export class MsisdnService {
  private readonly url = `${environment.apiUrl}/api/msisdns`;

  constructor(private readonly httpAuth: HttpAuth) {}

  public getUsageDetails(msisdn: string) {
    return this.httpAuth.get<DashboardResponse>(`${this.url}/${msisdn}/dashboard`);
  }

  public getCurrentSubscription(msisdn: string) {
    return this.httpAuth.get<SubscriptionResponse>(`${this.url}/${msisdn}/subscription`);
  }

  public getContract(msisdn: string) {
    return this.httpAuth.get<ContractResponse>(`${this.url}/${msisdn}/contract`);
  }

  public getCosts(msisdn: string) {
    return this.httpAuth.get<CostBreakdownResponse>(`${this.url}/${msisdn}/subscriptions`);
  }

  public getInternetUsageList(msisdn: string, from: string, to: string, isOnlyExtraCosts: boolean) {
    return this.httpAuth.get<InvoiceInternetDetailsResponse>(
      `${this.url}/${msisdn}/usage/data?from=${from}&to=${to}&isOnlyExtraCosts=${isOnlyExtraCosts}`
    );
  }

  public getCallSmsUsageList(msisdn: string, from: string, to: string, isOnlyExtraCosts: boolean) {
    return this.httpAuth.get<InvoiceCallDetailsResponse>(
      `${this.url}/${msisdn}/usage/voice?from=${from}&to=${to}&isOnlyExtraCosts=${isOnlyExtraCosts}`
    );
  }

  public getAllInvoices(msisdn: string) {
    return this.httpAuth.get<CustomerInvoiceResponse[]>(
      `${this.url}/${msisdn}/invoices/top?number=${3}`
    );
  }

  public getOutstandingInvoices(msisdn: string) {
    return this.httpAuth.get<CustomerInvoiceResponse[]>(
      `${this.url}/${msisdn}/invoices/outstanding`
    );
  }

  public getPaidInvoices(msisdn: string) {
    return this.httpAuth.get<CustomerInvoiceResponsePagedResponse>(
      `${this.url}/${msisdn}/invoices/paid?pageNumber=${1}&pageSize=${10}`
    );
  }

  public payInvoice(invoiceId: string, msisdn: string) {
    return this.httpAuth.get<{ url: string }>(
      `${this.url}/${msisdn}/invoices/link?invoice=${invoiceId}`
    );
  }

  public getServiceSettings(msisdn: string) {
    return this.httpAuth.get<NetworkSettingsResponse>(`${this.url}/${msisdn}/networkSettings`);
  }

  public updateServiceSettings(msisdn: string, body: NetworkSettingsRequest) {
    return this.httpAuth.put(`${this.url}/${msisdn}/networkSettings`, body);
  }

  public getSimType(msisdn: string) {
    return this.httpAuth.get<SimCardTypeResponse>(`${this.url}/${msisdn}/type`);
  }

  public getPukcode(msisdn: string) {
    return this.httpAuth.get<string>(`${this.url}/${msisdn}/puk`);
  }

  public getEsimProfile(msisdn: string) {
    return this.httpAuth.get<string>(`${this.url}/${msisdn}/esim-profile`);
  }

  public getPrivacyDetails(msisdn: string) {
    return this.httpAuth.get<PrivacyResponse>(`${this.url}/${msisdn}/privacy`);
  }

  public getAdSettings(msisdn: string) {
    return this.httpAuth.get<CommunicationResponse>(`${this.url}/${msisdn}/communications`);
  }

  public updateAdSettings(msisdn: string, body: CommunicationRequest) {
    return this.httpAuth.put(`${this.url}/${msisdn}/communications`, body).pipe(
      map(() => {
        throw new Error();
      })
    );
  }

  public replaceSimcard(msisdn: string, isEsimCard: boolean) {
    return this.httpAuth.put<any>(`${this.url}/${msisdn}/card?toEsim=${isEsimCard}`);
  }

  public blockSimcard(msisdn: string) {
    return this.httpAuth.put(`${this.url}/${msisdn}/block`);
  }

  public unblockSimcard(msisdn: string) {
    return this.httpAuth.put(`${this.url}/${msisdn}/resumeBlock`);
  }

  public activateSimcard(msisdn: string, customerId: number) {
    return this.httpAuth.get<{ url?: string }>(
      `${this.url}/${msisdn}/card/activation?customerId=${customerId}`
    );
  }

  public getCurrentBundlesList(msisdn: string) {
    return this.httpAuth.get<SubscriptionAvailabilityResponse>(`${this.url}/${msisdn}/bundles`);
  }

  public getBankAccountNumber(msisdn: string) {
    return this.httpAuth.get<BillingResponse>(`${this.url}/${msisdn}/billing`);
  }

  public changeBankAccount(msisdn: string) {
    return this.httpAuth.put<{ url: string }>(`${this.url}/${msisdn}/iban`);
  }

  public getAvailableSubscription(msisdn: string) {
    return this.httpAuth.get<NewSubscriptionsResponse>(
      `${this.url}/${msisdn}/subscriptions/available`
    );
  }

  public getWallet(msisdn: string) {
    return this.httpAuth.get<WalletResponse>(`${this.url}/${msisdn}/wallet`);
  }

  public topUpWallet(msisdn: string, amount: number) {
    return this.httpAuth.post<{ url: string }>(`${this.url}/${msisdn}/wallet/topup`, {
      amount,
    });
  }

  public topUpExtraBundle(bundleId: number, msisdn: string, isRoaming: boolean = false) {
    return this.httpAuth.post<{ url: string }>(
      `${this.url}/${msisdn}/extrabundle?subscriptionId=${bundleId}&isRoaming=${isRoaming}`
    );
  }

  public getWalletInternetUsage(msisdn: string, from: string, to: string, sortType: string) {
    return this.httpAuth.get<InvoiceInternetDetailsResponse>(
      `${this.url}/${msisdn}/wallet/usage/data?from=${from}&to=${to}&sortType=${sortType}`
    );
  }

  public getWalletCallSmsUsage(msisdn: string, from: string, to: string, sortType: string) {
    return this.httpAuth.get<InvoiceCallDetailsResponse>(
      `${this.url}/${msisdn}/wallet/usage/voice?from=${from}&to=${to}&sortType=${sortType}`
    );
  }

  public getContractDetails(msisdn: string) {
    return this.httpAuth.getFile(`${this.url}/${msisdn}/documents/contract?format=pdf`);
  }

  public getUsageOverview(msisdn: string, from: string, to: string, isOnlyExtraCosts: boolean) {
    return this.httpAuth.getFile(
      `${this.url}/${msisdn}/documents/usageOverview?format=pdf&from=${from}&to=${to}&isOnlyExtraCosts=${isOnlyExtraCosts}`
    );
  }

  public getWalletUsageOverview(msisdn: string, from: string, to: string) {
    return this.httpAuth.getFile(
      `${this.url}/${msisdn}/documents/walletUsageOverview?format=pdf&from=${from}&to=${to}&isOnlyExtraCosts=${false}`
    );
  }

  public getCostsPdf(msisdn: string) {
    return this.httpAuth.getFile(`${this.url}/${msisdn}/documents/tariffSheet?format=pdf`);
  }

  public getPrivacyRecordsPdf(msisdn: string) {
    return this.httpAuth.getFile(`${this.url}/${msisdn}/documents/privacyRecords?format=pdf`);
  }

  public updateSubscription(msisdn: string, body: ChangeSubscriptionRequest) {
    return this.httpAuth.post(`${this.url}/${msisdn}/subscription`, body);
  }

  public getDataOutsideEu(msisdn: string) {
    return this.httpAuth.get<DataOutsideEUResponse>(`${this.url}/${msisdn}/dataOutsideEuBundles`);
  }

  public resolvePortingBlock(msisdn: string, portingOption: PortingOption) {
    return this.httpAuth.put(
      `${this.url}/${msisdn}/porting/change-scenario?scenario=${portingOption}`
    );
  }
}
